import React, { useRef, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { CloudUpload, Download } from '@mui/icons-material'
import API from '../../../../services/API'
import downloadCsv from '../../../../helpers/downloadCsv'
import { buildTemplateCsv, chunkRows, parseParticipantCsv } from './participantCsv'

// Parses the organiser's entry list in the browser (papaparse) and sends the
// rows as sequential JSON chunks: the first chunk replaces the stored list,
// later chunks append. Keeps each request under the server's 1mb JSON limit.
const UploadParticipants = ({ eventID, existingCount, onClose, onUploaded }) => {
  const [fileName, setFileName] = useState('')
  const [rows, setRows] = useState([])
  const [warnings, setWarnings] = useState([])
  const [errors, setErrors] = useState([])
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const fileInputRef = useRef(null)

  const parseFile = async (file) => {
    setFileName(file.name)
    setRows([])
    setWarnings([])
    setErrors([])
    setProgress(0)
    try {
      const parsed = await parseParticipantCsv(file)
      setRows(parsed.rows)
      setWarnings(parsed.warnings)
      setErrors(parsed.errors)
    } catch (err) {
      setErrors([String(err?.message || err)])
    }
  }

  const upload = async () => {
    setUploading(true)
    setErrors([])
    try {
      const chunks = chunkRows(rows)
      let lastResponse
      for (let i = 0; i < chunks.length; i++) {
        const res = await API.post(`/events/${eventID}/participants`, {
          ...(i === 0 ? { replace: true, fileName } : {}),
          rows: chunks[i],
        })
        if (res.status !== 200) {
          setErrors([
            res.data?.error ||
              'Upload incomplete — the list on the server may be partial. Upload the file again to replace it.',
          ])
          return
        }
        lastResponse = res
        setProgress(((i + 1) / chunks.length) * 100)
      }
      onUploaded(lastResponse.data.participantList)
      onClose()
    } catch (e) {
      console.error(e)
      setErrors([
        'Upload incomplete — the list on the server may be partial. Upload the file again to replace it.',
      ])
    } finally {
      setUploading(false)
    }
  }

  return (
    <Stack padding={2} gap={1.5} sx={{ minWidth: { sm: 480 } }}>
      <Stack gap={0.25}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Upload participant list
        </Typography>
        <Typography variant="caption" color="text.secondary">
          CSV or TSV entry list from the event organiser. Clinicians can then
          search it by bib number from an ePCR linked to this event.
        </Typography>
      </Stack>

      <input
        type="file"
        accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) parseFile(file)
          e.target.value = ''
        }}
        style={{ display: 'none' }}
        ref={fileInputRef}
      />
      <Box
        sx={{
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'grey.400',
          bgcolor: 'grey.50',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': { borderColor: 'anBlue.main', bgcolor: 'grey.100' },
        }}
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        <CloudUpload sx={{ fontSize: 32, mb: 1, color: 'anBlue.main' }} />
        <Typography variant="body2" fontWeight={600}>
          Select File
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {fileName || 'CSV or TSV'}
        </Typography>
      </Box>
      <Alert
        severity="info"
        icon={false}
        action={
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            startIcon={<Download />}
            onClick={() =>
              downloadCsv('event-participants-template.csv', buildTemplateCsv())
            }
            sx={{ whiteSpace: 'nowrap', alignSelf: 'center' }}
          >
            Download Template
          </Button>
        }
      >
        Not sure of the format? The template shows every accepted column,
        including the optional medical ones.
      </Alert>

      {rows.length > 0 && (
        <Typography variant="body2">
          <strong>Parsed:</strong> {rows.length} participants
        </Typography>
      )}

      {errors.length > 0 && (
        <Alert severity="error">
          <AlertTitle>Issues:</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {errors.slice(0, 20).map((er, i) => (
              <li key={i}>{er}</li>
            ))}
          </ul>
          {errors.length > 20 && <div>...and {errors.length - 20} more</div>}
        </Alert>
      )}

      {warnings.length > 0 && (
        <Alert severity="warning">
          <AlertTitle>Warnings:</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {warnings.slice(0, 20).map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
          {warnings.length > 20 && <div>...and {warnings.length - 20} more</div>}
        </Alert>
      )}

      {rows.length > 0 && errors.length === 0 && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Bib</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>DOB</TableCell>
                <TableCell>Club</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(0, 5).map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{row.number}</TableCell>
                  <TableCell>
                    {[row.firstName, row.lastName].filter(Boolean).join(' ')}
                  </TableCell>
                  <TableCell>
                    {row.dob ? moment(row.dob).format('DD/MM/YYYY') : ''}
                  </TableCell>
                  <TableCell>{row.club}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {rows.length > 5 && (
            <Typography variant="caption" color="text.secondary">
              ...and {rows.length - 5} more
            </Typography>
          )}

          {existingCount > 0 && (
            <Alert severity="warning">
              This will replace the existing list of {existingCount}{' '}
              participants.
            </Alert>
          )}

          {uploading && <LinearProgress variant="determinate" value={progress} />}

          <Stack direction="row" gap={1} justifyContent="flex-end">
            <Button onClick={onClose} disabled={uploading}>
              Cancel
            </Button>
            <Button onClick={upload} disabled={uploading} variant="contained">
              {uploading
                ? 'Uploading...'
                : existingCount > 0
                  ? 'Replace List'
                  : 'Upload List'}
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  )
}

export default UploadParticipants
